import { sivControllerSearchSivQueryOptions, type SivVehicle } from 'korint-sdk';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@folio/components';
import { Input, useFormField } from '@folio/components/forms';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { Button } from '../../ui/button';
import { Spinner } from '../../ui/spinner';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

// @debt The SIV DTO names the date 'firstRegistrationDate' while the matching Korint
// form field is 'firstCirculationDate' — map external → internal here so the
// rest of the component can speak the Korint vocabulary.
const SIV_FIELD_TO_KORINT_FIELD = {
  brand: 'brand',
  model: 'model',
  firstRegistrationDate: 'firstCirculationDate',
} as const;

const sivFields = Object.keys(SIV_FIELD_TO_KORINT_FIELD) as Array<
  keyof typeof SIV_FIELD_TO_KORINT_FIELD
>;

type VehicleRegistrationNumberInputProps = {
  placeholder?: string;
  icon?: React.ReactNode;
  /**
   * When true: the SIV result is not auto-applied to the form, the prefill auto-search is
   * disabled (returning to the step never silently overwrites manual entry), and the
   * result/error cards are not rendered. The parent owns the result via the callbacks.
   */
  managedExternally?: boolean;
  onVehicleFound?: (vehicle: SivVehicle) => void;
  onNotFound?: () => void;
};

export const VehicleRegistrationNumberInput = ({
  placeholder,
  icon,
  managedExternally = false,
  onVehicleFound,
  onNotFound,
}: VehicleRegistrationNumberInputProps) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const { name: currentFieldName } = useFormField();
  const basePath = currentFieldName.split('.').slice(0, -1).join('.');

  const fieldNameWithoutPath = currentFieldName.split('.').pop() || currentFieldName;
  const numericSuffix = fieldNameWithoutPath.match(/\d+$/)?.[0] || '';

  const toPath = (field: string) => {
    const fieldWithSuffix = `${field}${numericSuffix}`;
    return basePath ? `${basePath}.${fieldWithSuffix}` : fieldWithSuffix;
  };

  const watchedRegistrationNumber = form.watch(currentFieldName) as string | undefined;
  const searchValue = watchedRegistrationNumber ?? '';

  const {
    data: vehicle,
    isFetching: isSearching,
    isFetched,
    error,
    refetch,
  } = useQuery({
    ...sivControllerSearchSivQueryOptions({ registrationNumber: searchValue }),
    enabled: false,
  });

  // Auto-fire the SIV lookup when the registration number is prefilled (e.g. from AI
  // extraction writing the field asynchronously after mount). Subscribing to the watched
  // value ensures this runs whenever the field transitions from empty to a valid plate,
  // not just on mount.
  useEffect(() => {
    if (managedExternally) return;
    if (isFetched) return;
    if (!watchedRegistrationNumber) return;
    if (watchedRegistrationNumber.replace(/[^A-Za-z0-9]/g, '').length < 7) return;
    refetch();
  }, [managedExternally, isFetched, watchedRegistrationNumber, refetch]);

  const searchRegistrationNumber = async () => {
    if (!searchValue || searchValue.length < 7) {
      return;
    }

    const { data } = await refetch();
    if (managedExternally) {
      if (data) onVehicleFound?.(data);
      else onNotFound?.();
    }
  };

  // Non-managed (e.g. AI prefill) auto-applies the fetched vehicle. Managed mode applies
  // only on an explicit search above, so re-mounting with a cached result never clobbers edits.
  useEffect(() => {
    if (managedExternally) return;
    if (vehicle) updateFormWithVehicleData(vehicle);
  }, [vehicle]);

  const updateFormWithVehicleData = (vehicle: SivVehicle) => {
    if (vehicle.registrationNumber) {
      form.setValue(toPath('registrationNumber'), vehicle.registrationNumber, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (vehicle.brand) {
      form.setValue(toPath('brand'), vehicle.brand, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (vehicle.model) {
      form.setValue(toPath('model'), vehicle.model, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (vehicle.vehicleType) {
      form.setValue(toPath('vehicleType'), vehicle.vehicleType, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }

    if (vehicle.firstRegistrationDate) {
      form.setValue(toPath('firstCirculationDate'), vehicle.firstRegistrationDate, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  return (
    <div>
      <Stack gap={2} className="sm:flex-row mb-4">
        <Input
          {...form.register(currentFieldName)}
          className="flex-1"
          placeholder={placeholder}
          icon={icon}
          onKeyDown={e => {
            // Enter would submit the surrounding funnel form (full page reload); search instead.
            if (e.key === 'Enter') {
              e.preventDefault();
              searchRegistrationNumber();
            }
          }}
        />
        <Button
          type="button"
          onClick={searchRegistrationNumber}
          disabled={isSearching}
          className="gap-2 sm:w-auto w-full"
        >
          {isSearching && <Spinner className="h-5 w-5 text-white" />}
          {t('vehicleRegistrationInput.searchButton')}
        </Button>
      </Stack>
      {!managedExternally && error && !isSearching && (
        <div className="mt-2 border rounded-md bg-red-50 border-red-200">
          <div className="p-3">
            <Text inline size="sm" weight="medium" className="text-red-800">
              {t('vehicleRegistrationInput.notFoundError')}
            </Text>
          </div>
        </div>
      )}

      {!managedExternally && vehicle && (
        <Card>
          <CardHeader>{t('vehicleRegistrationInput.vehicleInfoTitle')}</CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                {sivFields.map(sivField => {
                  const value = _.get(vehicle, sivField);

                  return (
                    <TableRow key={sivField}>
                      <TableHead className="text-left w-1/3">
                        {t(`fields.${SIV_FIELD_TO_KORINT_FIELD[sivField]}.label`)}
                      </TableHead>
                      <TableCell className="text-left">
                        {value ? (
                          <div>{value}</div>
                        ) : (
                          <div className="italic text-muted-foreground">
                            {t('vehicleRegistrationInput.unavailable')}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
